import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  LayoutDashboard,
  Package,
  FolderTree,
  Warehouse,
  Boxes,
  LogOut,
  ShoppingCart,
  Users,
  UserCog,
  Contact,
  Tag,
  Percent,
  FileText,
  BookOpen,
  Image,
  Sparkles,
  Globe,
  Languages,
  Truck,
  Settings,
  Activity,
  Eye,
  Search,
  AlertTriangle,
  Hourglass,
  Mail,
  FileSpreadsheet,
  Store,
  Undo2,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { GlobalSearch } from "./GlobalSearch";
import { LiveVisitorsBadge } from "./LiveVisitorsBadge";
import { IntegrationStatusBadge } from "./IntegrationStatusBadge";
import { StaffMentionsDropdown } from "./StaffMentionsDropdown";
import { OrderNotifications } from "./OrderNotifications";

// `adminOnly` hides the entry from staff. This is cosmetic — the API enforces
// the same rule — but it keeps staff from walking into a page that would only
// show them errors.
type NavLeaf = {
  titleKey: string;
  titleFallback: string;
  url: string;
  icon: React.ElementType;
  adminOnly?: boolean;
};
type NavSection =
  | {
      titleKey: string;
      titleFallback: string;
      url: string;
      icon: React.ElementType;
      children?: never;
    }
  | {
      titleKey: string;
      titleFallback: string;
      icon: React.ElementType;
      url?: never;
      children: NavLeaf[];
    };

const NAV: NavSection[] = [
  { titleKey: "nav.dashboard", titleFallback: "Dashboard", url: "/", icon: LayoutDashboard },
  {
    titleKey: "nav.orders",
    titleFallback: "Orders",
    icon: ShoppingCart,
    children: [
      { titleKey: "nav.ordersAll", titleFallback: "Orders", url: "/orders", icon: ShoppingCart },
      {
        titleKey: "nav.ordersPending",
        titleFallback: "Pending orders",
        url: "/orders/pending",
        icon: AlertTriangle,
      },
      {
        titleKey: "nav.ordersAbandoned",
        titleFallback: "Abandoned carts",
        url: "/orders/abandoned-carts",
        icon: Hourglass,
      },
      {
        titleKey: "nav.withdrawals",
        titleFallback: "Atteikumi",
        url: "/withdrawals",
        icon: Undo2,
      },
    ],
  },
  {
    titleKey: "nav.catalog",
    titleFallback: "Catalog",
    icon: Boxes,
    children: [
      { titleKey: "nav.products", titleFallback: "Products", url: "/products", icon: Package },
      { titleKey: "nav.categories", titleFallback: "Categories", url: "/categories", icon: FolderTree },
      { titleKey: "nav.grapeVarieties", titleFallback: "Grape varieties", url: "/grape-varieties", icon: Tag },
      { titleKey: "nav.tags", titleFallback: "Tags", url: "/tags", icon: Tag },
      { titleKey: "nav.inventory", titleFallback: "Inventory", url: "/inventory", icon: Warehouse },
    ],
  },
  {
    titleKey: "nav.customers",
    titleFallback: "Customers",
    icon: Users,
    children: [
      { titleKey: "nav.customers", titleFallback: "Customers", url: "/customers", icon: Users },
      {
        titleKey: "nav.customerGroups",
        titleFallback: "Customer groups",
        url: "/customer-groups",
        icon: UserCog,
      },
    ],
  },
  {
    titleKey: "nav.marketing",
    titleFallback: "Marketing",
    icon: Percent,
    children: [
      { titleKey: "nav.promotions", titleFallback: "Promotions", url: "/promotions", icon: Percent },
      { titleKey: "nav.newsletter", titleFallback: "Jaunumu abonenti", url: "/newsletter", icon: Percent },
    ],
  },
  {
    titleKey: "nav.content",
    titleFallback: "Content",
    icon: FileText,
    children: [
      { titleKey: "nav.banners", titleFallback: "Banners", url: "/banners", icon: Image },
      { titleKey: "nav.collections", titleFallback: "Collections", url: "/collections", icon: Sparkles },
      // Pages editor hidden from the menu — static pages rarely change and don't
      // need their own config surface; ongoing content lives in Blog. Route +
      // editor stay wired (/pages) so it's reachable by URL if ever needed.
      // { titleKey: "nav.pages", titleFallback: "Pages", url: "/pages", icon: FileText },
      { titleKey: "nav.blog", titleFallback: "Blog", url: "/blog", icon: BookOpen },
      // Distinct from nav.team (/team), which manages panelis LOGINS. This one
      // edits the public team cards on the website.
      { titleKey: "nav.staff", titleFallback: "Komanda (mājaslapā)", url: "/staff", icon: Contact },
    ],
  },
  {
    titleKey: "nav.performance",
    titleFallback: "Performance",
    icon: Activity,
    children: [
      {
        titleKey: "nav.performanceVisitors",
        titleFallback: "Visitors",
        url: "/performance/visitors",
        icon: Eye,
      },
      {
        titleKey: "nav.performanceSearch",
        titleFallback: "Search",
        url: "/performance/search",
        icon: Search,
      },
      {
        titleKey: "nav.performanceIntegrations",
        titleFallback: "Integrations",
        url: "/integrations",
        icon: Activity,
      },
      {
        titleKey: "nav.communications",
        titleFallback: "Communications",
        url: "/communications",
        icon: Mail,
      },
    ],
  },
  {
    titleKey: "nav.settings",
    titleFallback: "Settings",
    icon: Settings,
    children: [
      { titleKey: "nav.storeSettings", titleFallback: "Veikala iestatījumi", url: "/store-settings", icon: Store },
      { titleKey: "nav.languages", titleFallback: "Valodas", url: "/languages", icon: Languages },
      { titleKey: "nav.couriers", titleFallback: "Couriers", url: "/couriers", icon: Truck },
      { titleKey: "nav.moneo", titleFallback: "Moneo ERP", url: "/moneo", icon: Boxes },
      { titleKey: "nav.catalogSettings", titleFallback: "Catalog import/export", url: "/catalog-settings", icon: FileSpreadsheet },
      { titleKey: "nav.team", titleFallback: "Team", url: "/team", icon: UserCog, adminOnly: true },
    ],
  },
];

export function AdminLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();

  // Drop admin-only entries for staff, and any group left empty as a result.
  // While the session is still resolving we keep the full nav: hiding first
  // and restoring a moment later makes the sidebar flicker for admins, and
  // showing a link early costs nothing because the API gates the data anyway.
  const hideAdminOnly = auth.status === "authenticated" && auth.user.role !== "admin";
  const nav: NavSection[] = !hideAdminOnly
    ? NAV
    : NAV.flatMap<NavSection>((section) => {
        if (!section.children) return [section];
        const children = section.children.filter((leaf) => !leaf.adminOnly);
        return children.length ? [{ ...section, children }] : [];
      });

  function onSignOut() {
    auth.signOut();
    navigate("/login", { replace: true });
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <Sidebar collapsible="icon">
          <SidebarHeader className="p-4">
            <div className="flex items-center gap-2">
              <div className="flex size-7 shrink-0 items-center justify-center rounded bg-primary font-display text-sm font-bold text-primary-foreground">
                V
              </div>
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-display text-base font-bold text-sidebar-foreground">
                  Vīnoga Admin
                </span>
                <span className="truncate text-xs text-sidebar-foreground/50">v0.1</span>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>{t("nav.management", "Management")}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {nav.map((section) => {
                    const sectionTitle = t(section.titleKey, section.titleFallback);
                    if (!section.children) {
                      return (
                        <SidebarMenuItem key={section.titleKey}>
                          <SidebarMenuButton asChild tooltip={sectionTitle}>
                            <NavLink
                              to={section.url}
                              end
                              className={({ isActive }) =>
                                isActive
                                  ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                                  : "hover:bg-sidebar-accent/50"
                              }
                            >
                              <section.icon className="size-4" />
                              <span>{sectionTitle}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    }

                    const isActive = section.children.some(
                      (c) =>
                        location.pathname === c.url ||
                        location.pathname.startsWith(c.url + "/")
                    );

                    return (
                      <Collapsible
                        key={section.titleKey + (section.url ?? "")}
                        asChild
                        defaultOpen={isActive}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton tooltip={sectionTitle}>
                              <section.icon className="size-4" />
                              <span>{sectionTitle}</span>
                              <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {section.children.map((leaf) => (
                                <SidebarMenuSubItem key={leaf.url}>
                                  <SidebarMenuSubButton asChild>
                                    <NavLink
                                      to={leaf.url}
                                      end
                                      className={({ isActive }) =>
                                        isActive
                                          ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                                          : "hover:bg-sidebar-accent/50"
                                      }
                                    >
                                      <leaf.icon className="size-3.5" />
                                      <span>{t(leaf.titleKey, leaf.titleFallback)}</span>
                                    </NavLink>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="p-3">
            {auth.status === "authenticated" && (
              <div className="flex flex-col gap-1">
                <span className="truncate font-body text-xs text-sidebar-foreground/60">
                  {auth.user.email}
                </span>
                <button
                  type="button"
                  onClick={onSignOut}
                  className="flex items-center gap-2 py-1 font-body text-xs text-sidebar-foreground/60 transition-colors hover:text-sidebar-foreground"
                >
                  <LogOut className="size-3.5" />
                  <span>{t("nav.signOut", "Sign out")}</span>
                </button>
              </div>
            )}
          </SidebarFooter>
        </Sidebar>

        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="flex h-12 items-center justify-between border-b border-border bg-background px-4">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <GlobalSearch />
            </div>
            <div className="flex items-center gap-2">
              <LiveVisitorsBadge />
              <IntegrationStatusBadge />
              <StaffMentionsDropdown />
              <OrderNotifications />
              <LanguageSwitcher />
            </div>
          </header>
          <div className="flex-1 overflow-auto p-4 sm:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
